import { useMutation, useQuery } from '@tanstack/react-query';
import {
    createEmailTemplate,
    createEmailTrigger,
    deleteEmailTemplate,
    deleteEmailTrigger,
    deleteEmailTriggerSend,
    deleteEmailSuppression,
    explainEmailTrigger,
    getEmailEnrollmentStatusValues,
    getEmailFields,
    getEmailHelpers,
    getEmailServerConfig,
    getEmailServerHealth,
    getEmailStatusTriggerSet,
    getEmailSuppressions,
    getEmailTemplates,
    getEmailTriggerPresets,
    getEmailTriggerLastRun,
    getEmailTriggers,
    getEmailTriggerSchema,
    getEmailTriggerSendById,
    getEmailTriggerSendFilterKeys,
    getEmailTriggerSettings,
    getEmailSendSettings,
    updateEmailSendSettings,
    setEmailSendingEnabled,
    flushEmailQueue,
    setEmailTriggerActive,
    previewEmailHelpers,
    previewEmailHelpersContext,
    previewEmailTemplate,
    queryEmailTriggerSends,
    refreshEmailTriggerSend,
    retryEmailTriggerSend,
    runEmailTriggersBackfill,
    sendManualEmailTrigger,
    saveEmailHelpers,
    saveEmailServerConfig,
    sendTestEmail,
    testEmailTrigger,
    testEmailTriggerBacStatus,
    updateEmailStatusTriggerSet,
    updateEmailSuppression,
    updateEmailTemplate,
    updateEmailTrigger,
    createEmailSuppression,
    previewEventEmailOperation,
    commitEventEmailOperation,
} from './emailApi';

export const createEmailHooks = ({ queryClient }) => {
    const useEmailServerConfig = () =>
        useQuery({
            queryKey: ['emailServerConfig'],
            queryFn: getEmailServerConfig,
            retry: false,
        });

    const useSaveEmailServerConfig = () =>
        useMutation({
            mutationFn: saveEmailServerConfig,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailServerConfig'] });
            },
        });

    const useEmailServerHealth = () =>
        useMutation({
            mutationFn: getEmailServerHealth,
        });

    const useEmailTemplates = () =>
        useQuery({
            queryKey: ['emailTemplates'],
            queryFn: getEmailTemplates,
        });

    const useCreateEmailTemplate = () =>
        useMutation({
            mutationFn: createEmailTemplate,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
            },
        });

    const useUpdateEmailTemplate = () =>
        useMutation({
            mutationFn: updateEmailTemplate,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
            },
        });

    const useDeleteEmailTemplate = () =>
        useMutation({
            mutationFn: deleteEmailTemplate,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
            },
        });

    const usePreviewEmailTemplate = () =>
        useMutation({
            mutationFn: previewEmailTemplate,
        });

    const useSendTestEmail = () =>
        useMutation({
            mutationFn: sendTestEmail,
        });

    const useEmailHelpers = () =>
        useQuery({
            queryKey: ['emailHelpers'],
            queryFn: getEmailHelpers,
            retry: false,
        });

    const useSaveEmailHelpers = () =>
        useMutation({
            mutationFn: saveEmailHelpers,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailHelpers'] });
            },
        });

    const usePreviewEmailHelpers = () =>
        useMutation({
            mutationFn: previewEmailHelpers,
        });

    const usePreviewEmailHelpersContext = () =>
        useMutation({
            mutationFn: previewEmailHelpersContext,
        });

    const useEmailFields = () =>
        useMutation({
            mutationFn: getEmailFields,
        });

    const useEmailTriggers = () =>
        useQuery({
            queryKey: ['emailTriggers'],
            queryFn: getEmailTriggers,
        });

    const useEmailTriggerSchema = (params = {}) =>
        useQuery({
            queryKey: ['emailTriggerSchema', params],
            queryFn: () => getEmailTriggerSchema(params),
            enabled: Boolean(params?.bac && params?.programName),
        });

    const useEmailTriggerSettings = () =>
        useQuery({
            queryKey: ['emailTriggerSettings'],
            queryFn: getEmailTriggerSettings,
        });

    const useEmailSendSettings = () =>
        useQuery({
            queryKey: ['emailSendSettings'],
            queryFn: getEmailSendSettings,
        });

    const useEmailSuppressions = (options = {}) =>
        useQuery({
            queryKey: ['emailSuppressions', options],
            queryFn: () => getEmailSuppressions(options),
        });

    const useCreateEmailSuppression = () =>
        useMutation({
            mutationFn: createEmailSuppression,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailSuppressions'] });
            },
        });

    const useUpdateEmailSuppression = () =>
        useMutation({
            mutationFn: updateEmailSuppression,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailSuppressions'] });
            },
        });

    const useDeleteEmailSuppression = () =>
        useMutation({
            mutationFn: deleteEmailSuppression,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailSuppressions'] });
            },
        });

    const useEmailTriggerLastRun = () =>
        useQuery({
            queryKey: ['emailTriggerLastRun'],
            queryFn: getEmailTriggerLastRun,
        });

    const useEmailStatusTriggerSet = () =>
        useQuery({
            queryKey: ['emailStatusTriggerSet'],
            queryFn: getEmailStatusTriggerSet,
        });

    const useUpdateEmailStatusTriggerSet = () =>
        useMutation({
            mutationFn: updateEmailStatusTriggerSet,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailStatusTriggerSet'] });
            },
        });

    const useEmailTriggerPresets = () =>
        useQuery({
            queryKey: ['emailTriggerPresets'],
            queryFn: getEmailTriggerPresets,
        });

    const useCreateEmailTrigger = () =>
        useMutation({
            mutationFn: createEmailTrigger,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggers'] });
            },
        });

    const useUpdateEmailTrigger = () =>
        useMutation({
            mutationFn: updateEmailTrigger,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggers'] });
            },
        });

    const useSetEmailTriggerActive = () =>
        useMutation({
            mutationFn: setEmailTriggerActive,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggers'] });
            },
        });

    const useUpdateEmailSendSettings = () =>
        useMutation({
            mutationFn: updateEmailSendSettings,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailSendSettings'] });
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSettings'] });
            },
        });

    const useSetEmailSendingEnabled = () =>
        useMutation({
            mutationFn: setEmailSendingEnabled,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailSendSettings'] });
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSettings'] });
            },
        });

    const useFlushEmailQueue = () =>
        useMutation({
            mutationFn: flushEmailQueue,
        });

    const useRunEmailTriggersBackfill = () =>
        useMutation({
            mutationFn: runEmailTriggersBackfill,
        });

    const useDeleteEmailTrigger = () =>
        useMutation({
            mutationFn: deleteEmailTrigger,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggers'] });
            },
        });

    const useTestEmailTrigger = () =>
        useMutation({
            mutationFn: testEmailTrigger,
        });

    const useEmailEnrollmentStatusValues = () =>
        useQuery({
            queryKey: ['emailEnrollmentStatusValues'],
            queryFn: getEmailEnrollmentStatusValues,
            staleTime: 5 * 60 * 1000,
        });

    const useTestEmailTriggerBacStatus = () =>
        useMutation({
            mutationFn: testEmailTriggerBacStatus,
        });

    const useExplainEmailTrigger = () =>
        useMutation({
            mutationFn: explainEmailTrigger,
        });

    const useEmailTriggerSends = (payload = {}) =>
        useQuery({
            queryKey: ['emailTriggerSends', payload],
            queryFn: () => queryEmailTriggerSends(payload),
            keepPreviousData: true,
        });

    const useEmailTriggerSendFilterKeys = (options = {}) =>
        useQuery({
            queryKey: ['emailTriggerSendFilterKeys', options],
            queryFn: () => getEmailTriggerSendFilterKeys(options),
            staleTime: 5 * 60 * 1000,
        });

    const useEmailTriggerSendDetail = (id) =>
        useQuery({
            queryKey: ['emailTriggerSend', id],
            queryFn: () => getEmailTriggerSendById(id),
            enabled: Boolean(id),
        });

    const useDeleteEmailTriggerSend = () =>
        useMutation({
            mutationFn: deleteEmailTriggerSend,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSends'] });
            },
        });

    const useRetryEmailTriggerSend = () =>
        useMutation({
            mutationFn: ({ id, options }) => retryEmailTriggerSend(id, options),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSends'] });
            },
        });

    const useRefreshEmailTriggerSend = () =>
        useMutation({
            mutationFn: ({ id }) => refreshEmailTriggerSend(id),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSends'] });
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSend'] });
            },
        });

    const usePreviewEventEmailOperation = () =>
        useMutation({
            mutationFn: previewEventEmailOperation,
        });

    const useCommitEventEmailOperation = () =>
        useMutation({
            mutationFn: commitEventEmailOperation,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSends'] });
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSend'] });
            },
        });

    const useSendManualEmailTrigger = () =>
        useMutation({
            mutationFn: (payload) => sendManualEmailTrigger(payload),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['emailTriggerSends'] });
            },
        });

    return {
        useEmailServerConfig,
        useSaveEmailServerConfig,
        useEmailServerHealth,
        useEmailTemplates,
        useCreateEmailTemplate,
        useUpdateEmailTemplate,
        useDeleteEmailTemplate,
        usePreviewEmailTemplate,
        useSendTestEmail,
        useEmailHelpers,
        useSaveEmailHelpers,
        usePreviewEmailHelpers,
        usePreviewEmailHelpersContext,
        useEmailFields,
        useEmailTriggers,
        useEmailTriggerSchema,
        useEmailTriggerSettings,
        useEmailSendSettings,
        useEmailSuppressions,
        useCreateEmailSuppression,
        useUpdateEmailSuppression,
        useDeleteEmailSuppression,
        useEmailTriggerLastRun,
        useEmailStatusTriggerSet,
        useUpdateEmailStatusTriggerSet,
        useEmailTriggerPresets,
        useCreateEmailTrigger,
        useUpdateEmailTrigger,
        useSetEmailTriggerActive,
        useDeleteEmailTrigger,
        useTestEmailTrigger,
        useEmailEnrollmentStatusValues,
        useTestEmailTriggerBacStatus,
        useExplainEmailTrigger,
        useEmailTriggerSends,
        useEmailTriggerSendFilterKeys,
        useEmailTriggerSendDetail,
        useDeleteEmailTriggerSend,
        useRetryEmailTriggerSend,
        useRefreshEmailTriggerSend,
        usePreviewEventEmailOperation,
        useCommitEventEmailOperation,
        useSendManualEmailTrigger,
        useUpdateEmailSendSettings,
        useSetEmailSendingEnabled,
        useFlushEmailQueue,
        useRunEmailTriggersBackfill,
    };
};
